import React from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaUsers, FaMapMarkerAlt, FaPhone, FaFax, FaCalendar, FaYenSign, FaBriefcase, FaCertificate } from 'react-icons/fa';

const CompanyStrengths = () => {
  return (
    <motion.main 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 0.5 }}
      className="pt-32 sm:pt-40 pb-16 min-h-screen bg-gradient-to-b from-gray-50 to-blue-50" // pt-32 と sm:pt-40 を追加
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1 
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-center text-gray-800 mb-12 sm:mb-16"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          会社案内
        </motion.h1>

        <CEOGreeting />
        <CompanyOverview />
        <BusinessContent />
        <BranchOffice />
        <License />
      </div>
    </motion.main>
  );
};

const CEOGreeting = () => {
  return (
    <section className="mb-16 sm:mb-24">
      <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>代表取締役挨拶</h2>
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-6 sm:p-8 rounded-lg shadow-2xl">
        <div className="flex flex-col md:flex-row items-center mb-6 sm:mb-8">
          <img className="w-32 h-32 sm:w-48 sm:h-48 rounded-full object-cover mb-4 md:mb-0 md:mr-8" src="/images/ceo-image.jpg" alt="CEO" />
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>松野 初夫</h3>
            <p className="text-lg sm:text-xl">代表取締役</p>
          </div>
        </div>
        <div className="space-y-4 text-sm sm:text-lg leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          <p>
            平成18年に生まれ故郷である南相馬市で独立・開業し、【地域密着型】・【迅速なレスポンス】・【ニーズに合わせた最適な提案】この３点を念頭に、お客様からの安心・信頼を得られるよう取り組んでまいりました。
          </p>
          <p>
            人工知能の進化・環境エネルギー問題など、ものづくりを取り巻く環境は今後大きく変化していく可能性がある中においても、業界全体の最新情報を収集し、いち早くお客様へ合わせた最適なご案内を心掛けております。
          </p>
          <p>
            地域密着を心掛け、「ものづくり」を頑張る企業様から、各メーカ様とのつなぎ役に「共伸機工」を選んで頂けるよう、日々努力を重ねてまいります。
          </p>
        </div>
      </div>
    </section>
  );
};

const CompanyOverview = () => {
  const companyInfo = [
    { icon: <FaBuilding />, label: '会社名', value: '共伸機工株式会社' },
    { icon: <FaUsers />, label: '代表者', value: '代表取締役　松野 初夫' },
    { icon: <FaMapMarkerAlt />, label: '所在地', value: '〒975-0032　福島県南相馬市原町区桜井町1丁目4番地' },
    { icon: <FaPhone />, label: 'TEL', value: '0244-25-4182' },
    { icon: <FaFax />, label: 'FAX', value: '0244-25-4183' },
    { icon: <FaCalendar />, label: '設立', value: '平成18年12月12日' },
    { icon: <FaYenSign />, label: '資本金', value: '3,200,000円' },
    { icon: <FaUsers />, label: '従業員数', value: '8名' },
  ];

  return (
    <section className="mb-16 sm:mb-24">
      <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>会社概要</h2>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-8">
          {companyInfo.map((item, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center space-x-4 bg-blue-50 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <span className="text-3xl sm:text-4xl text-blue-500">{item.icon}</span>
              <div>
                <p className="font-semibold text-gray-700 text-base sm:text-lg">{item.label}</p>
                <p className="text-gray-600 text-sm sm:text-lg">{item.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BusinessContent = () => {
  const businessItems = [
    '工作機械・中古機械販売',
    '測定工具販売',
    '切削工具販売・特注カッター販売',
    '周辺機器',
    '伝動省力機器',
    'その他',
  ];

  return (
    <section className="mb-16 sm:mb-24">
      <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>事業内容</h2>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="p-4 sm:p-8">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {businessItems.map((item, index) => (
              <motion.li 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 bg-blue-50 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <FaBriefcase className="text-2xl sm:text-3xl text-blue-500" />
                <span className="text-gray-700 text-sm sm:text-lg">{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const BranchOffice = () => {
  const offices = [
    {
      name: '本社',
      manager: '松野 初夫',
      address: '〒975-0032　福島県南相馬市原町区桜井町1丁目4番地',
      tel: '0244-25-4182',
      fax: '0244-25-4183',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3159.3830691520016!2d140.97336817644324!3d37.6401971720198!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60209666b35c94bf%3A0xd0fd50bc92d8e137!2z44CSOTc1LTAwMzIg56aP5bO255yM5Y2X55u46aas5biC5Y6f55S65Yy65qGc5LqV55S677yR5LiB55uu77yU!5e0!3m2!1sja!2sjp!4v1724389583177!5m2!1sja!2sjp',
      imageUrl: '/images/honsha.png'
    },
    {
      name: 'いわき営業所',
      manager: '高木 和弘',
      address: '福島県いわき市小名浜住吉字搦32-5 IAビル 1-C',
      tel: '0246-76-0388',
      fax: '0246-58-9255',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3187.2265121332875!2d140.88351627641782!3d36.98052607219631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60211252bd2dccf5%3A0x1d9e15c11fb05eab!2z44CSOTcxLTgxMjQg56aP5bO255yM44GE44KP44GN5biC5bCP5ZCN5rWc5L2P5ZCJ5pCm77yT77yS4oiS77yVIO-8qe-8oeODk-ODqw!5e0!3m2!1sja!2sjp!4v1724389875843!5m2!1sja!2sjp',
      imageUrl: '/images/iwaki.png'
    }
  ];

  return (
    <section className="mb-16 sm:mb-24">
      <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>営業所</h2>
      {offices.map((office, index) => (
        <div key={index} className="bg-white shadow-2xl rounded-lg overflow-hidden mb-8">
          <div className="md:flex">
            <div className="md:flex-shrink-0">
              <img src={office.imageUrl} alt={office.name} className="h-48 w-full object-cover md:w-64 md:h-64" />
            </div>
            <div className="p-4 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">{office.name}</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-2"><strong>所在地:</strong> {office.address}</p>
              <p className="text-sm sm:text-base text-gray-600"><strong>TEL:</strong> {office.tel}</p>
              <p className="text-sm sm:text-base text-gray-600"><strong>FAX:</strong> {office.fax}</p>
            </div>
          </div>
          <div className="h-48 sm:h-64 w-full">
            <iframe
              src={office.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`${office.name}の地図`}
            ></iframe>
          </div>
        </div>
      ))}
    </section>
  );
};

const License = () => {
  return (
    <section>
      <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>ライセンス</h2>
      <div className="bg-white shadow-2xl rounded-lg p-4 sm:p-8">
        <div className="flex items-center justify-center space-x-4">
          <FaCertificate className="text-4xl sm:text-5xl text-blue-500" />
          <span className="text-lg sm:text-2xl text-gray-700">古物商許可証 交付　第251280000624</span>
        </div>
      </div>
    </section>
  );
};

export default CompanyStrengths;